import { extractPermissionsFromAccessRoles } from '@/util/permissionFunctions';
import {
  AccessRolesPermissionPermissionEnum,
  Employee,
  EmployeeAccessRolesPermissionNameEnum,
  EmployeeAccessRolesPermissionScopeEnum,
} from '../../../api/v1';

export const Permission = AccessRolesPermissionPermissionEnum;
export const CustomPermissionNameEnum = EmployeeAccessRolesPermissionNameEnum;
export const AdvancedPermissionScopeEnum =
  EmployeeAccessRolesPermissionScopeEnum;

/**
 * Check if the candidate employee has a particular permission
 */
const employeeHasPermission = (
  employee: Employee,
  permission: AccessRolesPermissionPermissionEnum,
): boolean =>
  extractPermissionsFromAccessRoles(employee.accessRoles).includes(permission);

/**
 * Check if the candidate employee can line manage the target employee
 */
const employeeLineManagesTarget = (employee: Employee, target: Employee) =>
  employee.id === target.lineManagerId;

/**
 * Check if the candidate employee has the right scope to manage the target employee
 */
const employeeHasScopeForPermission = (
  employee: Employee,
  permission: EmployeeAccessRolesPermissionNameEnum,
  targetEmployee?: Employee,
) => {
  const permissionOverride = employee.permissionOverrides.find(
    (override) => override.name === permission,
  );

  return (
    // If no override exists accept it
    !permissionOverride ||
    // Otherwise check the scope of the override
    permissionOverride.scope === AdvancedPermissionScopeEnum.All ||
    (targetEmployee &&
      permissionOverride.scope === AdvancedPermissionScopeEnum.LineManagees &&
      employeeLineManagesTarget(employee, targetEmployee)) ||
    (targetEmployee &&
      permissionOverride.scope === AdvancedPermissionScopeEnum.Custom &&
      permissionOverride.employeeIds.includes(targetEmployee.id))
  );
};

export const useUserCan = (loggedInEmployee: Employee) => {
  return {
    managePayRates: (targetEmployee: Employee) =>
      employeeHasPermission(loggedInEmployee, Permission.ManageEmployeePay) &&
      employeeHasScopeForPermission(
        loggedInEmployee,
        CustomPermissionNameEnum.ManageEmployeePay,
        targetEmployee,
      ),
    viewEmployeeCosts: (targetEmployee: Employee) =>
      employeeHasPermission(loggedInEmployee, Permission.ViewEmployeeCosts) &&
      employeeHasScopeForPermission(
        loggedInEmployee,
        CustomPermissionNameEnum.ViewEmployeeCosts,
        targetEmployee,
      ),
    viewLabourCosts: () =>
      employeeHasPermission(loggedInEmployee, Permission.ViewLabourCosts) &&
      employeeHasScopeForPermission(
        loggedInEmployee,
        CustomPermissionNameEnum.ViewLabourCosts,
      ),
  };
};
